// airflowPipelinesService.js
import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js';

export const airflowPipelinesService = {
  /**
   * List all available MWAA environments in the AWS account
   * GET /api/v1/airflow/pipelines/environments
   */
  getEnvironments: async (awsAccountId = null, clientId = null) => {
    const params = new URLSearchParams();
    if (awsAccountId) params.append("aws_account_id", awsAccountId);
    if (clientId) params.append("client_id", clientId);
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/environments${params.toString() ? `?${params}` : ""}`
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to list Airflow environments: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Discover all DAGs from an MWAA environment
   * GET /api/v1/airflow/pipelines/discover?environment_name=xxx
   */
  discoverDags: async (environmentName, awsAccountId = null, environmentRegion = null, clientId = null) => {
    if (!environmentName) throw new Error('environment_name is required');
    const params = new URLSearchParams({ environment_name: environmentName });
    if (awsAccountId) params.append("aws_account_id", awsAccountId);
    if (environmentRegion) params.append("environment_region", environmentRegion);
    if (clientId) params.append("client_id", clientId);
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/discover?${params}`
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to discover DAGs: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Enable monitoring for an Airflow DAG
   * POST /api/v1/airflow/pipelines/enable?environment_name=xxx&aws_account_id=xxx
   */
  enableDag: async (environmentName, awsAccountId, { dag_id, enable_logs = true, enable_metrics = true }, clientId = null) => {
    if (!environmentName || !awsAccountId || !dag_id) {
      throw new Error('environment_name, aws_account_id, and dag_id are required');
    }
    const params = new URLSearchParams({
      environment_name: environmentName,
      aws_account_id: awsAccountId,
    });
    if (clientId) params.append("client_id", clientId);
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/enable?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dag_id, enable_logs, enable_metrics }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to enable DAG: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Disable monitoring for an Airflow DAG
   * POST /api/v1/airflow/pipelines/disable?dag_id=xxx&environment_name=xxx&aws_account_id=xxx
   */
  disableDag: async (dagId, environmentName, awsAccountId, clientId = null) => {
    if (!dagId || !environmentName || !awsAccountId) {
      throw new Error('dag_id, environment_name, and aws_account_id are required');
    }
    const params = new URLSearchParams({
      dag_id: dagId,
      environment_name: environmentName,
      aws_account_id: awsAccountId,
    });
    if (clientId) params.append("client_id", clientId);
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/disable?${params}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to disable DAG: ${errorText}`);
    }
    return await response.json();
  },

  // ---------- Phase 2: DAG Detail, Runs, Metrics, Trigger ----------

  _mwaaQueryParams(environmentName, { awsAccountId, environmentRegion, clientId } = {}) {
    const params = new URLSearchParams({ environment_name: environmentName });
    if (awsAccountId) params.append('aws_account_id', awsAccountId);
    if (environmentRegion) params.append('environment_region', environmentRegion);
    if (clientId) params.append('client_id', clientId);
    return params;
  },

  /**
   * Get DAG detail (metadata, config, last run status)
   * GET /api/v1/airflow/pipelines/{dag_id}/detail?environment_name=xxx
   */
  getDagDetail: async (dagId, environmentName, awsAccountId = null, environmentRegion = null, clientId = null) => {
    if (!dagId || !environmentName) throw new Error('dag_id and environment_name are required');
    const params = airflowPipelinesService._mwaaQueryParams(environmentName, {
      awsAccountId,
      environmentRegion,
      clientId,
    });
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/${encodeURIComponent(dagId)}/detail?${params}`
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get DAG detail: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Get paginated DAG run history
   * GET /api/v1/airflow/pipelines/{dag_id}/runs?environment_name=xxx&limit=50&offset=0
   */
  getDagRuns: async (
    dagId,
    environmentName,
    { limit = 50, offset = 0, awsAccountId = null, environmentRegion = null, clientId = null } = {}
  ) => {
    if (!dagId || !environmentName) throw new Error('dag_id and environment_name are required');
    const params = airflowPipelinesService._mwaaQueryParams(environmentName, {
      awsAccountId,
      environmentRegion,
      clientId,
    });
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/${encodeURIComponent(dagId)}/runs?${params}`
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get DAG runs: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Get DAG metrics (success rate, durations, health score, time-series)
   * GET /api/v1/airflow/pipelines/{dag_id}/metrics?environment_name=xxx&days=7
   */
  getDagMetrics: async (
    dagId,
    environmentName,
    days = 7,
    awsAccountId = null,
    environmentRegion = null,
    clientId = null
  ) => {
    if (!dagId || !environmentName) throw new Error('dag_id and environment_name are required');
    const params = airflowPipelinesService._mwaaQueryParams(environmentName, {
      awsAccountId,
      environmentRegion,
      clientId,
    });
    params.append('days', String(days));
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/${encodeURIComponent(dagId)}/metrics?${params}`
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get DAG metrics: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Trigger a DAG run
   * POST /api/v1/airflow/pipelines/{dag_id}/trigger?environment_name=xxx
   */
  triggerDagRun: async (
    dagId,
    environmentName,
    { logical_date, conf, note, awsAccountId = null, environmentRegion = null, clientId = null } = {}
  ) => {
    if (!dagId || !environmentName) throw new Error('dag_id and environment_name are required');
    const params = airflowPipelinesService._mwaaQueryParams(environmentName, {
      awsAccountId,
      environmentRegion,
      clientId,
    });
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/airflow/pipelines/${encodeURIComponent(dagId)}/trigger?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logical_date: logical_date || null,
          conf: conf || {},
          note: note || '',
        }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to trigger DAG run: ${errorText}`);
    }
    return await response.json();
  },
};
